import { FormEvent, useContext, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Info, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Toggle from '@/components/ui/Toggle';
import Tooltip from '@/components/ui/Tooltip';
import { LanguageContext } from '@/context/LanguageContext';
import {
  VodHarvestingConfiguration,
  applicationSettingsService,
} from '@/services/applicationSettingsService';

const QUERY_KEY = ['application-settings'] as const;

const DEFAULT_VOD_HARVESTING: VodHarvestingConfiguration = {
  detectionEnabled: true,
  enforcementEnabled: false,
  shortWindowSeconds: 30,
  requestWindowSeconds: 60,
  longWindowMinutes: 60,
  holdDurationMinutes: 10,
  maxRequestsPerWindow: 12,
  maxDistinctContentPerShortWindow: 4,
  maxRequestsPerLongWindow: 120,
  maxDistinctContentPerLongWindow: 30,
};

const HarvestingSettings = ({ onDirty, onSaving }: { onDirty: (dirty: boolean) => void; onSaving: (saving: boolean) => void }) => {
  const { t } = useContext(LanguageContext)!;
  const copy = t.applicationSettings;
  const queryClient = useQueryClient();
  const [vodHarvesting, setVodHarvesting] = useState<VodHarvestingConfiguration>(
    DEFAULT_VOD_HARVESTING
  );

  const settingsQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: applicationSettingsService.getSettings,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (settingsQuery.data) {
      setVodHarvesting(settingsQuery.data.vodHarvesting);
    }
  }, [settingsQuery.data]);

  const updateSettings = useMutation({
    mutationFn: applicationSettingsService.updateSettings,
    onSuccess: (settings) => {
      queryClient.setQueryData(QUERY_KEY, settings);
      onDirty(false);
      toast.success(copy.saved);
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : copy.saveError);
    },
  });

  useEffect(() => { onSaving(updateSettings.isPending); return () => onSaving(false); }, [updateSettings.isPending, onSaving]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    updateSettings.mutate({ vodHarvesting });
  };

  const setHarvestingValue = <K extends keyof VodHarvestingConfiguration>(
    key: K,
    value: VodHarvestingConfiguration[K]
  ) => {
    onDirty(true);
    setVodHarvesting((current) => ({ ...current, [key]: value }));
  };

  const numberField = (
    key: keyof Omit<VodHarvestingConfiguration, 'detectionEnabled' | 'enforcementEnabled'>,
    label: string,
    help: string,
    min: number,
    max: number,
    unit: string
  ) => (
    <div>
      <div className="mb-1 flex items-center gap-1.5 text-sm font-medium text-gray-700">
        <span>{label}</span>
        <Tooltip content={help}>
          <Info
            className="h-4 w-4 cursor-help text-gray-400 hover:text-indigo-600"
            aria-label={copy.vodHarvesting.moreInformation}
          />
        </Tooltip>
      </div>
      <Input
        type="number"
        value={vodHarvesting[key]}
        min={min}
        max={max}
        required
        append={unit}
        disabled={settingsQuery.isLoading || updateSettings.isPending}
        onChange={(event) => setHarvestingValue(key, Number(event.target.value))}
      />
    </div>
  );

  return (
    <div>
      <form onSubmit={submit}>
        {settingsQuery.isError && <p role="alert" className="text-red-600">{copy.loadError}</p>}
        <section>
          <h2 className="text-xl font-semibold text-gray-900">{copy.vodHarvesting.title}</h2>
          <p className="mt-1 text-sm text-gray-500">{copy.vodHarvesting.description}</p>

          <div className="mt-5 space-y-4">
            <Toggle
              checked={vodHarvesting.detectionEnabled}
              onChange={(checked) => setHarvestingValue('detectionEnabled', checked)}
              label={copy.vodHarvesting.detectionEnabled}
              helperText={copy.vodHarvesting.detectionHelp}
              disabled={settingsQuery.isLoading || updateSettings.isPending}
            />
            <Toggle
              checked={vodHarvesting.enforcementEnabled}
              onChange={(checked) => setHarvestingValue('enforcementEnabled', checked)}
              label={copy.vodHarvesting.enforcementEnabled}
              helperText={copy.vodHarvesting.enforcementHelp}
              disabled={
                !vodHarvesting.detectionEnabled || settingsQuery.isLoading || updateSettings.isPending
              }
            />
          </div>

          <h3 className="mt-7 text-base font-semibold text-gray-900">
            {copy.vodHarvesting.thresholds}
          </h3>
          <div className="mt-4 grid gap-5 sm:grid-cols-2">
            {numberField('shortWindowSeconds', copy.vodHarvesting.shortWindow, copy.vodHarvesting.shortWindowHelp, 5, 3600, copy.seconds)}
            {numberField('requestWindowSeconds', copy.vodHarvesting.requestWindow, copy.vodHarvesting.requestWindowHelp, 5, 3600, copy.seconds)}
            {numberField('longWindowMinutes', copy.vodHarvesting.longWindow, copy.vodHarvesting.longWindowHelp, 1, 1440, copy.minutes)}
            {numberField('holdDurationMinutes', copy.vodHarvesting.holdDuration, copy.vodHarvesting.holdDurationHelp, 1, 1440, copy.minutes)}
            {numberField('maxRequestsPerWindow', copy.vodHarvesting.maxRequestsWindow, copy.vodHarvesting.maxRequestsWindowHelp, 1, 10000, copy.requests)}
            {numberField('maxDistinctContentPerShortWindow', copy.vodHarvesting.maxDistinctShort, copy.vodHarvesting.maxDistinctShortHelp, 1, 1000, copy.items)}
            {numberField('maxRequestsPerLongWindow', copy.vodHarvesting.maxRequestsLong, copy.vodHarvesting.maxRequestsLongHelp, 1, 100000, copy.requests)}
            {numberField('maxDistinctContentPerLongWindow', copy.vodHarvesting.maxDistinctLong, copy.vodHarvesting.maxDistinctLongHelp, 1, 10000, copy.items)}
          </div>
        </section>

        <div className="mt-8 border-t border-gray-200 pt-5">
          <Button
            type="submit"
            loading={updateSettings.isPending}
            disabled={settingsQuery.isLoading || settingsQuery.isError}
          >
            <Save className="mr-2 h-4 w-4" aria-hidden="true" />
            {copy.save}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default HarvestingSettings;
